import React, { useState, useEffect } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppTextField } from "components/common/inputs";
import { AppDialog } from "components/common/dialogs";
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import {
  Security as SecurityIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import "./PermissionOverview.css";
import { getToken, getUser } from "services/auth.service";
import { usePageTitle } from "context/PageTitleContext";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { getActionColor, getModuleShortName, getActionLabel, getRoleColor } from "modules/systems/utils/permission.utils";
import DataTable from "components/common/tables/DataTable"
import { AppTab, AppTabPanel } from "components/common/tabs";
import { AppStatCard, AppStatGrid } from "components/common/cards";
import { AppButton } from "components/common/buttons";

const PermissionOverview = () => {
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [permissionsTableKey, setPermissionsTableKey] = useState(0);

  const tabs = [
    { label: "Quyền hạn", icon: <SecurityIcon />, value: 0 },
    { label: "Vai trò", icon: <GroupIcon />, value: 1 },
    { label: "Người dùng", icon: <PersonIcon />, value: 2 },
  ];

  // Modal dialog hiển thị danh sách user của vai trò
  const renderViewUsersDialog = () => {
    if (!viewUsersDialogOpen) return null;
    // Filter theo search
    const filtered = usersOfRole.filter(
      (u) =>
        u.userName?.toLowerCase().includes(usersOfRoleSearch.toLowerCase()) ||
        u.fullName?.toLowerCase().includes(usersOfRoleSearch.toLowerCase())
    );
    // Phân trang
    const columns = [
      { accessorKey: "userName", header: "Tên đăng nhập", size: 80 },
      { accessorKey: "fullName", header: "Tên đầy đủ", size: 120 },
    ];
    return (
      <AppDialog
        open={viewUsersDialogOpen}
        onClose={() => setViewUsersDialogOpen(false)}
        title={`Người dùng của vai trò: ${selectedRoleForUsers?.roleName || ""}`}
        size="md"
        actions={
          <>
            <AppButton variant="outlined" color="inherit" onClick={() => setViewUsersDialogOpen(false)}>
              Đóng
            </AppButton>
          </>
        }
      >
          <AppTextField
            label="Tìm kiếm người dùng"
            value={usersOfRoleSearch}
            onChange={(_, e) => {
              setUsersOfRoleSearch(e.target.value);
              setUsersOfRolePage(0);
            }}
            fullWidth
            margin="normal"
          />
          <DataTable
            columns={columns}
            data={filtered}
            enableColumnActions={false}
            enableRowSelection={false}
            initialState={{
              pagination: { pageIndex: 0, pageSize: usersOfRoleRowsPerPage },
              density: "compact",
            }}
          />
      </AppDialog>
    );
  };
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPermission, setEditPermission] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [rolePermDialogOpen, setRolePermDialogOpen] = useState(false);
  const [selectedRoleForPerm, setSelectedRoleForPerm] = useState(null);
  const [selectedRolePerms, setSelectedRolePerms] = useState([]);
  const [loadingRolePerms, setLoadingRolePerms] = useState(false);
  const [rolePermsFetched, setRolePermsFetched] = useState(false);
  // State cho modal xem người dùng của vai trò
  const [viewUsersDialogOpen, setViewUsersDialogOpen] = useState(false);
  const [selectedRoleForUsers, setSelectedRoleForUsers] = useState(null);
  const [usersOfRole, setUsersOfRole] = useState([]);
  const [loadingUsersOfRole, setLoadingUsersOfRole] = useState(false);
  const [usersOfRoleSearch, setUsersOfRoleSearch] = useState("");
  const [usersOfRolePage, setUsersOfRolePage] = useState(0);
  const [usersOfRoleRowsPerPage, setUsersOfRoleRowsPerPage] = useState(10);

  // Set page title
  useEffect(() => {
    setPageTitle("Phân quyền");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true);
    try {
      await Promise.all([
        loadPermissions(),
        loadRoles(),
        loadUsers(),
        loadUserRoles(),
        loadRolePermissions(),
        loadUserPermissions(),
      ]);
    } catch (error) {
      showSnackbar("Lỗi khi tải dữ liệu", "error");
    } finally {
      setLoading(false);
    }
  }

  async function loadPermissions() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/SystemPermissions/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const json = await response.json();
        setPermissions(json.data || json.Data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading permissions:", error);
    }
  }

  async function loadRoles() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolesManagement?pageSize=1000&pageNumber=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading roles:", error);
    }
  }

  async function loadUsers() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/User?pageSize=1000&pageNumber=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading users:", error);
    }
  }

  async function loadUserRoles() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole?pageSize=1000&pageNumber=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading user roles:", error);
    }
  }

  async function loadRolePermissions() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolePermission?pageSize=1000&pageNumber=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRolePermissions(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading role permissions:", error);
    }
  }

  async function loadUserPermissions() {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserPermission?pageSize=1000&pageNumber=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading user permissions:", error);
    }
  }

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setSearchTerm("");
    if (newValue === 0) {
      setPermissionsTableKey((k) => k + 1);
      loadPermissions();
    }
  };

  // Hàm xử lý khi nhấn nút xem người dùng của vai trò
  const handleViewUsersOfRole = (role) => {
    setSelectedRoleForUsers(role);
    setViewUsersDialogOpen(true);
    setLoadingUsersOfRole(true);
    // Lấy danh sách user có vai trò này
    const roleId = role?.roleId ?? role?.RoleId ?? role?.id ?? role?.Id;
    // Lọc userRoles để lấy userId
    const userIds = userRoles
      .filter((ur) => String(ur.roleId) === String(roleId))
      .map((ur) => ur.userId);
    // Lọc users theo userIds
    const usersList = users.filter((u) => userIds.includes(u.userId));
    setUsersOfRole(usersList);
    setLoadingUsersOfRole(false);
    setUsersOfRoleSearch("");
    setUsersOfRolePage(0);
  };

  const handleViewRolePermissions = async (role) => {
    const roleName = (role?.roleName || role?.RoleName || "").toLowerCase();
    if (roleName === "admin") {
      showSnackbar("Vai trò Admin có toàn quyền", "info");
      return;
    }

    const roleIdValue = role?.roleId ?? role?.RoleId ?? role?.id ?? role?.Id;


    setSelectedRoleForPerm(role);
    setRolePermDialogOpen(true);
    setLoadingRolePerms(true);
    setRolePermsFetched(false);
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_API_URL
        }/api/RolePermission/role/${encodeURIComponent(
          String(roleIdValue ?? "")
        )}?pageSize=1000&pageNumber=1`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = resp.ok ? await resp.json() : null;
      const fresh = json ? json.data || json.Data || [] : rolePermissions;
      if (resp.ok) {
        const isRoleScoped =
          (Array.isArray(fresh) &&
            fresh.length > 0 &&
            String(
              fresh[0]?.roleId ?? fresh[0]?.RoleId ?? fresh[0]?.roleID ?? ""
            ) === String(roleIdValue)) ||
          String(url).includes("/role/");

        if (isRoleScoped && roleIdValue != null) {
          setRolePermissions((prev = []) => {
            const filtered = prev.filter(
              (rp) =>
                String(rp?.roleId ?? rp?.RoleId ?? rp?.id ?? "") !==
                String(roleIdValue)
            );
            return [...filtered, ...(fresh || [])];
          });
        } else {
          setRolePermissions(fresh);
        }
      }

      const rpForRole = (fresh || rolePermissions).filter((rp) => {
        const rid =
          rp?.roleId ?? rp?.RoleId ?? rp?.RoleID ?? rp?.roleID ?? rp?.roleId;
        return String(rid) === String(roleIdValue);
      });

      const perms = rpForRole.map((rp) => {
        const rpPermId =
          rp?.permissionId ??
          rp?.PermissionId ??
          rp?.PermissionID ??
          rp?.permissionID ??
          rp?.permId;
        const perm = permissions.find((p) => {
          const pid =
            p?.permissionId ??
            p?.PermissionId ??
            p?.PermissionID ??
            p?.permissionID ??
            p?.id ??
            p?.Id;
          return String(pid) === String(rpPermId);
        });

        if (perm) {
          return {
            permissionId: perm?.permissionId ?? perm?.PermissionId ?? perm?.id,
            permissionName:
              perm?.permissionName ?? perm?.PermissionName ?? perm?.name ?? "",
            description: perm?.description ?? perm?.Description ?? "",
          };
        }

        return {
          permissionId:
            rpPermId ?? rp?.permissionId ?? rp?.PermissionId ?? rp?.id,
          permissionName:
            rp?.permissionName ?? rp?.PermissionName ?? String(rpPermId ?? ""),
          description: rp?.description ?? rp?.Description ?? "",
        };
      });

      const finalPerms = Array.isArray(perms) ? perms : [];


      setSelectedRoleForPerm(role);
      setSelectedRolePerms(Array.isArray(finalPerms) ? [...finalPerms] : []);

      setRolePermsFetched(true);
      setLoadingRolePerms(false);
    } catch (err) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại.", "error");
      console.error("Error loading role permissions:", err);
      const rpForRole = rolePermissions.filter(
        (rp) =>
          String(rp?.roleId ?? rp?.RoleId ?? rp?.id) === String(roleIdValue)
      );
      const perms = rpForRole.map((rp) => ({
        permissionId: rp?.permissionId ?? rp?.PermissionId ?? rp?.id,
        permissionName:
          rp?.permissionName ??
          rp?.PermissionName ??
          String(rp?.permissionId ?? ""),
        description: rp?.description ?? rp?.Description ?? "",
      }));
      setSelectedRoleForPerm(role);
      setSelectedRolePerms(perms);
      setRolePermsFetched(true);
      setLoadingRolePerms(false);
    }
  };

  const getPermissionStats = () => {
    const totalPermissions = permissions.length;
    const usedPermissions = new Set([
      ...rolePermissions.map((rp) => rp.permissionId),
      ...userPermissions.map((up) => up.permissionId),
    ]).size;
    return {
      total: totalPermissions,
      used: usedPermissions,
      unused: totalPermissions - usedPermissions,
    };
  };

  const getRoleStats = () => {
    const totalRoles = roles.length;
    const usedRoles = new Set(userRoles.map((ur) => ur.roleId)).size;
    return {
      total: totalRoles,
      used: usedRoles,
      unused: totalRoles - usedRoles,
    };
  };

  const getUserStats = () => {
    const totalUsers = users.length;
    const activeUsers = users.filter((u) => u.isActive).length;
    const usersWithRoles = new Set(userRoles.map((ur) => ur.userId)).size;

    return {
      total: totalUsers,
      active: activeUsers,
      withRoles: usersWithRoles,
      withoutRoles: totalUsers - usersWithRoles,
    };
  };

  const handleSaveEdit = async () => {
    if (!editPermission) return;
    setSavingEdit(true);
    try {
      const token = getToken();
      const user = getUser();
      const updateBy = user?.userName || user?.userId || "system";

      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/SystemPermissions/${editPermission.permissionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ description: editDescription, updateBy }),
        }
      );

      if (!resp.ok) throw new Error("Update failed");

      setPermissions((prev) =>
        prev.map((p) =>
          p.permissionId === editPermission.permissionId
            ? { ...p, description: editDescription }
            : p
        )
      );
      showSnackbar("Cập nhật mô tả quyền thành công", "success");
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error saving edit:", error);
      showSnackbar("Lưu thay đổi thất bại", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  // Hàm xử lý chỉnh sửa mô tả quyền với kiểm tra phân quyền
  const handleEditPermission = async (permission) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (result.hasPermission) {
      setEditPermission(permission);
      setEditDescription(permission.description || "");
      setEditDialogOpen(true);
    } else {
      showSnackbar("Bạn không có quyền chỉnh sửa mô tả quyền.", "warning");
    }
  };

  const renderOverviewCards = () => {
    const permissionStats = getPermissionStats();
    const roleStats = getRoleStats();
    const userStats = getUserStats();

    return (
      <AppStatGrid>
        <AppStatCard
          label="Quyền hạn"
          value={permissionStats.total}
          icon={<SecurityIcon />}
          rows={[
            { label: "Đã sử dụng", value: permissionStats.used, color: "#16a34a" },
            { label: "Chưa sử dụng", value: permissionStats.unused, color: "#f59e0b" },
          ]}
        />

        <AppStatCard
          label="Vai trò"
          value={roleStats.total}
          icon={<GroupIcon />}
          rows={[
            { label: "Đã gán", value: roleStats.used, color: "#7c3aed" },
            { label: "Chưa gán", value: roleStats.unused, color: "#64748b" },
          ]}
        />

        <AppStatCard
          label="Người dùng"
          value={userStats.total}
          icon={<PersonIcon />}
          rows={[
            { label: "Hoạt động", value: userStats.active, color: "#16a34a" },
            { label: "Có vai trò", value: userStats.withRoles, color: "#2563eb" },
          ]}
        />
      </AppStatGrid>
    );
  };

  const renderPermissionsTab = () => {
    const filteredPermissions = permissions.filter(
      (permission) =>
        permission.permissionName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        permission.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
      { accessorKey: "permissionId", header: "ID" },
      {
        accessorKey: "stt", header: "STT", size: 30,
        Cell: ({ row }) => row.index + 1
      },
      {
        accessorKey: "permissionDisplayName", header: "Tên quyền", size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
      },
      { accessorKey: "permissionCode", header: "Mã quyền", size: 240, },
      {
        accessorKey: "description", header: "Mô tả", size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
      },
      {
        accessorKey: "moduleCode",
        header: "Ứng dụng",
        Cell: ({ cell }) => (
          <Chip label={getModuleShortName(cell.getValue())} size="small" variant="outlined" />
        ),
        size: 90,
      },
      {
        accessorKey: "actionCode",
        header: "Hành động",
        Cell: ({ cell }) => (
          <Chip
            label={getActionLabel(cell.getValue())}
            size="small"
            color={getActionColor(cell.getValue())}
          />
        ),
        size: 80,
      },
      {
        id: "used",
        header: "Sử dụng",
        accessorFn: (row) =>
          rolePermissions.some((rp) => rp.permissionId === row.permissionId) ||
          userPermissions.some((up) => up.permissionId === row.permissionId),
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? "Đã sử dụng" : "Chưa sử dụng"}
            size="small"
            color={cell.getValue() ? "success" : "default"}
          />
        ),
        size: 120,
      },
      {
        id: "actions",
        header: "Thao tác",
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <Tooltip title="Chỉnh sửa">
              <IconButton
                size="small"
                onClick={() => handleEditPermission(row.original)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        size: 60,
      },
    ];

    return (
      <DataTable
        key={`permissions-table-${permissionsTableKey}`}
        columns={columns}
        data={filteredPermissions}
        initialState={{
          columnVisibility: { permissionId: false },
          pagination: { pageIndex: 0, pageSize: 10 },
          density: "compact",
        }}
      />
    );
  };

  const renderRolesTab = () => {
    const filteredRoles = roles.filter(
      (role) =>
        role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
      { accessorKey: "roleId", header: "ID", size: 60 },
      {
        accessorKey: "stt", header: "STT",
        Cell: ({ row }) => row.index + 1
      },
      {
        accessorKey: "roleName",
        header: "Tên vai trò",
        Cell: ({ row }) => (
          <Chip
            label={row.original.roleName}
            size="small"
            sx={
              getRoleColor(row.original.roleName)
                ? { backgroundColor: getRoleColor(row.original.roleName), color: "#fff" }
                : {}
            }
            variant={getRoleColor(row.original.roleName) ? "filled" : "outlined"}
          />
        ),
      },
      {
        accessorKey: "description", header: "Mô tả",
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
      },
      {
        id: "permissionCount",
        header: "Số quyền",
        accessorFn: (row) =>
          rolePermissions.filter((rp) => rp.roleId === row.roleId).length,
        Cell: ({ row, cell }) => {
          const roleName = (row?.original?.roleName || "").toLowerCase();
          if (roleName === "admin")
            return <Chip label="Toàn quyền" size="small" color="success" />;
          return (
            <Chip
              label={String(cell.getValue())}
              size="small"
              color="primary"
            />
          );
        },
      },
      {
        id: "userCount",
        header: "Số người dùng",
        accessorFn: (row) =>
          userRoles.filter((ur) => ur.roleId === row.roleId).length,
        Cell: ({ cell }) => (
          <Chip label={cell.getValue()} size="small" color="secondary" />
        ),
      },
      {
        id: "status",
        header: "Trạng thái",
        accessorFn: (row) =>
          userRoles.filter((ur) => ur.roleId === row.roleId).length > 0,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? "Đang sử dụng" : "Chưa sử dụng"}
            size="small"
            color={cell.getValue() ? "success" : "default"}
          />
        ),
      },
      {
        id: "actions",
        header: "Thao tác",
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Tooltip title="Xem quyền">
              <IconButton
                size="small"
                onClick={() => handleViewRolePermissions(row.original)}
              >
                <SecurityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Xem người dùng">
              <IconButton
                size="small"
                onClick={() => handleViewUsersOfRole(row.original)}
              >
                <PersonIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        size: 80,
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={filteredRoles}
        enableColumnActions={false}
        enableRowSelection={false}
        initialState={{
          columnVisibility: { roleId: false },
          pagination: { pageIndex: 0, pageSize: 10 },
        }}
      />
    );
  };

  const renderUsersTab = () => {
    const filteredUsers = users.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
      { accessorKey: "userId", header: "ID", size: 60 },
      {
        accessorKey: "stt", header: "STT",
        Cell: ({ row }) => row.index + 1
      },
      {
        accessorKey: "fullName",
        header: "Tên đầy đủ",
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
        Cell: ({ row }) => (
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {row.original.fullName}
          </Typography>
        ),
      },
      {
        accessorKey: "userName",
        header: "Tên đăng nhập",
        Cell: ({ row }) => row.original.userName,
      },
      { accessorKey: "email", header: "Email" },
      {
        id: "roles",
        header: "Vai trò",
        accessorFn: (row) => {
          // Lấy danh sách roleId của user
          const roleIds = userRoles
            .filter((ur) => ur.userId === row.userId)
            .map((ur) => ur.roleId);
          // Lấy tên vai trò từ danh sách roleId
          return roles
            .filter((r) => roleIds.includes(r.roleId))
            .map((r) => r.roleName);
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value || value.length === 0)
            return <Chip label="Không có" size="small" color="default" />;
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              {value.map((role, idx) => (
                <Chip
                  key={idx}
                  label={role}
                  size="small"
                  sx={
                    getRoleColor(role)
                      ? { backgroundColor: getRoleColor(role), color: "#fff" }
                      : {}
                  }
                  variant={getRoleColor(role) ? "filled" : "outlined"}
                />
              ))}
            </Box>
          );
        },
      },
      {
        id: "status",
        header: "Trạng thái",
        accessorFn: (row) => !!row.isActive,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? "Đã kích hoạt" : "Chưa kích hoạt"}
            size="small"
            sx={{
              backgroundColor: cell.getValue() ? "#4caf50" : "#ff9800",
              color: "#fff",
            }}
          />
        ),
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={filteredUsers}
        enableColumnActions={false}
        enableRowSelection={false}
        initialState={{
          columnVisibility: { userId: false },
          columnPinning: { left: ["stt", "fullName"] },
          pagination: { pageIndex: 0, pageSize: 10 },
        }}
      />
    );
  };

  return (
    <Box className="permission-overview tab-content">
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {renderOverviewCards()}
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <AppTab
            value={tabValue}
            onChange={handleTabChange}
            tabs={tabs}
          />
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexDirection: "row-reverse",
            }}
          >
          </Box>
          <AppTabPanel value={tabValue} index={0}>
            {tabValue === 0 && renderPermissionsTab()}
          </AppTabPanel>

          <AppTabPanel value={tabValue} index={1}>
            <>
              {renderRolesTab()}
              {renderViewUsersDialog()}
            </>
          </AppTabPanel>

          <AppTabPanel value={tabValue} index={2}>
            {tabValue === 2 && renderUsersTab()}
          </AppTabPanel>
        </Box>
      </Paper>
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <AppDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Chỉnh sửa mô tả quyền"
        size="sm"
        loading={savingEdit}
        actions={
          <>
            <AppButton disabled={savingEdit} variant="outlined" onClick={() => setEditDialogOpen(false)}>Hủy</AppButton>
            <AppButton
              onClick={handleSaveEdit}
              variant="contained"
              disabled={savingEdit}
            >
              Lưu
            </AppButton>
          </>
        }
      >
          <AppTextField
            disabled={savingEdit}
            fullWidth
            multiline
            minRows={3}
            value={editDescription}
            onChange={(_, e) => setEditDescription(e.target.value)}
            label="Mô tả"
            variant="outlined"
            sx={{
              "& .MuiInputLabel-root": {
                backgroundColor: "background.paper",
                px: 0.5,
              },
              marginTop: "8px",
            }}
          />
      </AppDialog>

      <AppDialog
        open={rolePermDialogOpen}
        onClose={() => setRolePermDialogOpen(false)}
        title={`Quyền của vai trò ${selectedRoleForPerm?.roleName || ""}`}
        size="md"
        loading={loadingRolePerms}
        actions={
          <>
            <AppButton disabled={loadingRolePerms} variant="outlined" onClick={() => setRolePermDialogOpen(false)}>
              Đóng
            </AppButton>
          </>
        }
      >
          {loadingRolePerms && (
            <Typography variant="body2">Đang tải quyền...</Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Hiển thị {selectedRolePerms.length} quyền
          </Typography>
          <DataTable
            columns={[
              { accessorKey: "permissionId", header: "ID", size: 60 },
              {
                accessorKey: "permissionName",
                header: "Tên quyền",
                size: 240,
              },
              { accessorKey: "description", header: "Mô tả", size: 300 },
            ]}
            data={selectedRolePerms}
            getRowId={(row) => row.permissionId}
            enableColumnActions={false}
            initialState={{
              pagination: { pageIndex: 0, pageSize: 10 },
              density: "compact",
            }}
          />

      </AppDialog>
    </Box>
  );
};

export default PermissionOverview;
